import { Beaker } from "lucide-react";
import React, { useState } from "react";
import { WastePagination } from "../../../../components/wasteManagment/components/WastePagination";
import { Badge } from "../../../../components/ui/common/badge";
import { PAGINATION_ITEMS_PER_PAGE } from "../../../../config/constants";
import CollapsibleSectionCard from "../shared/components/CollapsibleSectionCard";
import { usePaginatedRecords } from "../shared/hooks/usePaginatedRecords";
import ChemicalAddRecordPanel from "./components/ChemicalAddRecordPanel";
import ChemicalRecordRow from "./components/ChemicalRecordRow";
import { useChemicalRecordOptions } from "./hooks/useChemicalRecordOptions";
import { ChemicalRecord, ChemicalSectionProps } from "./types/chemicalRecords.types";

/**
 * Chemicals sub-section for a process. Composition only — pagination, static-data
 * lookups, and record/route mapping each live in their own hook/util.
 */
const ChemicalSection: React.FC<ChemicalSectionProps> = ({
  records,
  disabled,
  onAdd,
  onUpdate,
  onRemove,
  onAddRoute,
  onUpdateRoute,
  onRemoveRoute
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftKey, setDraftKey] = useState(0);

  const { chemFunctionOptions, chemFunctionLabelMap, transportOptions, transportLabelMap } =
    useChemicalRecordOptions();
  const { page, setPage, totalPages, paginated, goToLastPageAfterAdd, clampAfterRemove } =
    usePaginatedRecords(records, PAGINATION_ITEMS_PER_PAGE);

  const handleAddSave = (patch: Partial<ChemicalRecord>) => {
    onAdd(patch);
    setDraftKey(k => k + 1);
    goToLastPageAfterAdd();
  };

  const handleEditSave = (id: string) => (patch: Partial<ChemicalRecord>) => {
    onUpdate(id, patch);
    setEditingId(null);
  };

  const handleDelete = (id: string) => {
    onRemove(id);
    setEditingId(null);
    clampAfterRemove();
  };

  return (
    <CollapsibleSectionCard
      icon={Beaker}
      title="Chemicals"
      disabled={disabled}
      badge={
        records.length > 0 && (
          <Badge variant="outline" className="tw-text-[10px] tw-h-5 tw-px-1.5">
            {records.length}
          </Badge>
        )
      }
    >
      {paginated.map((rec, idx) => (
        <ChemicalRecordRow
          key={rec.id}
          rec={rec}
          idx={(page - 1) * PAGINATION_ITEMS_PER_PAGE + idx}
          chemFunctionOptions={chemFunctionOptions}
          chemFunctionLabelMap={chemFunctionLabelMap}
          transportOptions={transportOptions}
          transportLabelMap={transportLabelMap}
          isEditing={editingId === rec.id}
          onEdit={() => setEditingId(rec.id)}
          onSave={handleEditSave(rec.id)}
          onCancelEdit={() => setEditingId(null)}
          onDelete={() => handleDelete(rec.id)}
          onAddRoute={onAddRoute}
          onUpdateRoute={onUpdateRoute}
          onRemoveRoute={onRemoveRoute}
        />
      ))}

      <WastePagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />

      <ChemicalAddRecordPanel
        draftKey={draftKey}
        hasRecords={records.length > 0}
        onSave={handleAddSave}
        chemFunctionOptions={chemFunctionOptions}
        transportOptions={transportOptions}
        transportLabelMap={transportLabelMap}
        onAddRoute={onAddRoute}
        onUpdateRoute={onUpdateRoute}
        onRemoveRoute={onRemoveRoute}
      />
    </CollapsibleSectionCard>
  );
};

export default ChemicalSection;
